<i18n src="./translations"></i18n>

<template>
  <section
    class="pdp-attributes"
    :class="classes"
    :data-preload="[$options.name]"
  >
    <PdpVehicleNotification
      class="pdp-attributes__vehicle-notification is-mobile"
      :gtm-class-name="'gtm--pdp-vehicle-notification'"
      :vehicle-compatibility="productVehicleCompatibility"
      :vehicle-name="vehicleName"
      :related-items-link="relatedItemsLink"
    />

    <div class="pdp-attributes__content">
      <div class="pdp-attributes__information">
        <PdpPriceInfo
          v-if="!isBusinessCustomer"
          class="pdp-attributes__price-info"
          :price="productPrice"
          :unit-prices="product.unitPrices"
          :prices="product.prices"
          :explanatory-information="productExplanatoryInformation"
          :has-extra-information="summaryHasExtraInformation"
          :has-tax-information="summaryHasTaxInformation"
        />

        <PriceBusinessCustomer v-if="isBusinessCustomer" :product="product" />

        <PdpAvailability
          :class="`qa-availability--${productAvailability}`"
          class="pdp-attributes__availability qa-PriceInfo__availabilty"
          :state="productAvailability"
        />

        <BranchesAvailabilityLink
          class="pdp-attributes__branch-availability-link qa-branchesAvailabilityLink"
          label-type="secondary"
          :has-location-image="false"
          :product="product"
        />
      </div>

      <PdpVehicleNotification
        class="pdp-attributes__vehicle-notification"
        :gtm-class-name="'gtm--pdp-vehicle-notification'"
        :vehicle-compatibility="productVehicleCompatibility"
        :vehicle-name="vehicleName"
        :related-items-link="relatedItemsLink"
      />

      <PdpActions
        class="pdp-attributes__actions"
        :quantity-options="productQuantityOptions"
        :quantity-amount="product.quantityAmount"
        :is-purchasable="isProductPurchasable"
        :add-to-cart-button-props="addToCartButtonProps"
        @cart-click="handleAddToCart"
        @wishlist-click="handleAddToWishlist"
        @quantity-change="handleQuantityChange"
      />
    </div>

    <Dialog
      ref="unavailableProductDialog"
      class="pdp-attributes__unavailable-product-dialog"
      :click-outside="true"
      :title="$t('pdp-attributes.availability.delayed.title')"
    >
      <div>
        {{
          $t('pdp-attributes.availability.delayed.message', {
            '%customerServicePhoneNumber%': customerServicePhoneNumber,
          })
        }}
      </div>
    </Dialog>
  </section>
</template>

<script>
import globalMixin from 'Libs/mixins/globalMixin';
import PdpVehicleNotification from 'Components/02-molecules/pdp-vehicle-notification/PdpVehicleNotification';
import PdpPriceInfo from 'Components/02-molecules/pdp-summarized-info/PdpSummarizedInfo';
import { mapActions, mapGetters, mapState } from 'vuex';
import PriceBusinessCustomer from 'Components/01-atoms/price-business-customer/PriceBusinessCustomer';
import PdpAvailability from 'Components/01-atoms/pdp-availability/PdpAvailability';
import BranchesAvailabilityLink from 'Components/01-atoms/branches-availability-link/BranchesAvailabilityLink';
import PdpActions from 'Components/02-molecules/pdp-actions/PdpActions';
import Dialog from 'Components/02-molecules/dialog/Dialog';

export default {
  name: 'PdpAttributes',
  components: {
    PdpPriceInfo,
    PriceBusinessCustomer,
    PdpVehicleNotification,
    PdpAvailability,
    PdpActions,
    BranchesAvailabilityLink,
    Dialog,
  },
  mixins: [globalMixin],
  props: {
    product: {
      type: Object,
      required: true,
      default: () => ({
        default: {
          productId: '',
          quantityOptions: [],
          unitPrices: [
            {
              label: '',
              price: '',
            },
          ],
          quantityThreshold: 0,
          quantityAmount: 0,
          isBuyable: false,
          availability: '',
          priceTexts: {
            depositPrice: 0,
            tax: 0,
            pricePerUnit: { value: 0, unit: 0 },
            discountedPricePerUnit: {
              unit: 'Liter',
              value: '7,07 €',
            },
          },
          sku: '',
          productName: '',
          brandName: '',
        },
      }),
    },
    relatedItemsLink: {
      type: String,
      default: '',
    },
    pdpCarBar: {
      type: Object,
      default: () => ({
        key: 'UNIVERSAL_PRODUCT',
      }),
    },
    summaryHasExtraInformation: {
      type: Boolean,
      default: true,
    },
    summaryHasTaxInformation: {
      type: Boolean,
      default: true,
    },
    isListingPage: { default: false, type: Boolean },
    isNewWishlist: { default: false, type: Boolean },
  },
  data: () => ({
    isAddToCartInProgress: false,
    selectedQuantity: 1,
  }),
  computed: {
    ...mapState('core', {
      viewport: ({ viewport }) => viewport.range,
      companyId: (state) => state.requestData.companyId,
    }),
    ...mapGetters('core', ['customerServicePhoneNumber']),
    ...mapState('carSelection', {
      vehicleName: ({ data: { vehicle = {} } }) => {
        const { manufacturer, model, type, bodyCodes } = vehicle;
        const selectionName = `${manufacturer} ${model} ${type}`;

        if (bodyCodes && bodyCodes.length > 0) {
          return `${selectionName} (${[...bodyCodes].sort().join(', ')})`;
        }
        return selectionName;
      },
    }),
    isBusinessCustomer() {
      return typeof this.companyId === 'string' && this.companyId !== '';
    },
    productExplanatoryInformation() {
      return this.product.priceTexts;
    },
    productPrice() {
      return this.product.unitPrices[0].price;
    },
    productVehicleCompatibility() {
      return this.pdpCarBar?.key || 'UNIVERSAL_PRODUCT';
    },
    productAvailability() {
      return this.product.availability;
    },
    productSKU() {
      return this.product.sku;
    },
    productBrandName() {
      return this.product.brandName;
    },
    productName() {
      return this.product.productName;
    },
    productQuantityOptions() {
      return this.product.quantityOptions;
    },
    isProductPurchasable() {
      return this.product.isBuyable;
    },
    addToCartButtonProps() {
      if (this.isAddToCartInProgress) {
        return {
          class: 'button--japaneseLaurel',
          title: this.$t('pdp-attributes.cart.added'),
          icon: 'added_to_cart_check',
          disabled: true,
        };
      }

      return {
        class: [
          this.viewport && this.viewport !== 'desktop' && 'button--noHover',
          'qa-addToCartButton',
        ],
        icon: 'cart',
        title: this.$t('pdp-attributes.cart.add'),
      };
    },
  },
  mounted() {
    this.selectedQuantity = this.product.quantityAmount;
  },
  methods: {
    ...mapActions('miniCart', ['addToCart']),
    ...mapActions('wishlist', ['addToWishlist']),
    async handleAddToCart() {
      const productPriceEqualsZero = this.product.prices.price === 0;

      const availabilityStatus = [
        'DELAYED',
        'AVAILABLE_SOON',
        'AVAILABLE_ON_REQUEST',
        'OUT_OF_STOCK',
        'NOT_AVAILABLE',
        'NOTAVAILABLE',
        '2TO4WEEKS',
      ];

      if (
        productPriceEqualsZero ||
        availabilityStatus.includes(this.productAvailability) ||
        availabilityStatus.includes(this.productAvailability.toUpperCase())
      ) {
        this.$refs.unavailableProductDialog.show();
      } else {
        this.isAddToCartInProgress = true;
        try {
          await this.addToCart({
            product: this.product,
            quantity: this.selectedQuantity,
          });
        } finally {
          setTimeout(() => (this.isAddToCartInProgress = false), 2000);
        }
      }
    },
    handleAddToWishlist() {
      this.isNewWishlist
        ? this.$emit('wish-click', true)
        : this.addToWishlist({
            product: this.product,
            quantity: this.selectedQuantity,
            list: this.isListingPage
              ? 'Product Listing Page'
              : 'Product Detail Page',
          });
    },
    handleQuantityChange(quantity) {
      this.selectedQuantity = quantity;
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';

.pdp-attributes {
  max-width: 400px;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    left: 0;
    overflow: hidden;
    border-left: var(--space-1--half) solid var(--color-white);
    border-top: var(--space-1--half) solid transparent;
    border-bottom: var(--space-1--half) solid transparent;
  }

  &__vehicle-notification {
    &:not(.is-mobile) {
      @media #{$_mediaMDown} {
        display: none;
      }
    }

    &.is-mobile {
      margin-bottom: var(--space-1--half);

      @media #{$_mediaLUp} {
        display: none;
      }
    }
  }
}

.pdp-attributes__content {
  padding: var(--space-2) var(--space-6) var(--space-3);
  border-bottom-right-radius: var(--border-radius-default);
  border-bottom-left-radius: var(--border-radius-default);
}

.pdp-attributes__branch-availability-link {
  padding: 0;
  margin-top: var(--space-0--half);
  font-size: var(--font-size-SM);
}

.pdp-attributes__actions {
  margin-top: var(--space-2);
}

@media #{$_mediaMDown} {
  .pdp-attributes {
    max-width: unset;
    width: 100%;

    &::after {
      display: none;
    }
  }

  .pdp-attributes__content {
    padding: var(--space-2);
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }
}

@media #{$_mediaLUp} {
  .pdp-attributes__content {
    min-height: 352px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}

@media #{$_mediaXLUp} {
  .pdp-attributes__content {
    min-height: 395px;
  }
}
</style>
